import { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import ApiService from '../ApiService';
import { geocodeAddress } from '../utils/geocoder';
import * as db from '../cache/db';

// Must match the server-side cell size used in lat_lng_to_cell()
const CELL_SIZE = 0.01;

// Survive Homepage remounts (e.g. user opens /offline and comes back) so the
// viewport isn't reset to user GPS each time.
const MAP_CENTER_KEY = 'homepage_map_center';
const MAP_ZOOM_KEY   = 'homepage_map_zoom';

function readPersistedCenter() {
  try {
    const raw = sessionStorage.getItem(MAP_CENTER_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return (typeof parsed?.lat === 'number' && typeof parsed?.lng === 'number') ? parsed : null;
  } catch { return null; }
}

function readPersistedZoom() {
  const raw = Number(sessionStorage.getItem(MAP_ZOOM_KEY));
  return Number.isFinite(raw) && raw > 0 ? raw : 14;
}

function latLngToCell(lat, lng) {
  return [Math.floor(lat / CELL_SIZE), Math.floor(lng / CELL_SIZE)];
}

const searchCacheKey = (uid, row, col, lang) =>
  `place_search_${uid}_${row}_${col}_${lang}`;

function buildSortedPlaces(rawPlaces, selectedList) {
  // Merge: always keep selected places visible even if not in raw API results
  const merged = new Map(rawPlaces);
  for (const [ref, place] of selectedList) {
    if (!merged.has(ref)) merged.set(ref, place);
  }

  return new Map([...merged].sort((a, b) => {
    const aSelected = selectedList.has(a[0]);
    const bSelected = selectedList.has(b[0]);
    if (aSelected !== bSelected) return Number(bSelected) - Number(aSelected);
    if (a[1].user_rating_count !== b[1].user_rating_count) {
      return b[1].user_rating_count - a[1].user_rating_count;
    }
    return a[1].description.name.localeCompare(b[1].description.name);
  }));
}

export default function usePlaceSearch({ user, i18n, routeMode, selectedList }) {
  // Raw API results only — selection and sorting are applied via useMemo below
  const [rawPlaces, setRawPlaces] = useState(new Map());
  const [loading, setLoading] = useState(false);
  const [zoom, setZoom] = useState(readPersistedZoom);
  const [mapCenter, setMapCenter] = useState(readPersistedCenter);

  useEffect(() => {
    try {
      if (mapCenter) sessionStorage.setItem(MAP_CENTER_KEY, JSON.stringify(mapCenter));
    } catch { /* storage may be unavailable in private mode */ }
  }, [mapCenter]);

  useEffect(() => {
    try { sessionStorage.setItem(MAP_ZOOM_KEY, String(zoom)); } catch { /* ignore */ }
  }, [zoom]);

  // Tracks the last search so we can skip redundant API calls when the map
  // stays within the same server-side grid cell and language hasn't changed.
  const lastSearchKey = useRef(null);

  // Derived display list: selected places always float to top, then by rating.
  // Recomputed when rawPlaces or selectedList changes — no API call needed for
  // selection changes.
  const places = useMemo(
    () => buildSortedPlaces(rawPlaces, selectedList),
    [rawPlaces, selectedList],
  );

  const fetchPlaces = useCallback(async () => {
    if (!mapCenter || routeMode) return;

    // Skip API call if we are still in the same server-side grid cell
    const [row, col] = latLngToCell(mapCenter.lat, mapCenter.lng);
    const searchKey = `${row},${col},${i18n.language}`;
    if (lastSearchKey.current === searchKey) return;
    lastSearchKey.current = searchKey;

    const cacheKey = searchCacheKey(user?.uid ?? 'anon', row, col, i18n.language);

    const applyIncoming = (data) => {
      const incoming = Array.isArray(data) ? data : [];
      const incomingMap = new Map(incoming.map(p => [p.reference, p]));
      setRawPlaces(prev => {
        const next = new Map();
        for (const [ref, newPlace] of incomingMap) {
          // Reuse existing object when the place is already known — keeps the
          // same reference in memory so React.memo on ImageWithToken sees
          // identical props and skips re-fetching the photo.
          next.set(ref, prev.has(ref) ? prev.get(ref) : newPlace);
        }
        return next;
      });
    };

    setLoading(true);
    try {
      const data = await ApiService.post('/place/search/', {
        longitude: mapCenter.lng,
        latitude:  mapCenter.lat,
        language:  i18n.language,
        offset:    0,
      }, user);

      if (data) {
        applyIncoming(data);
        db.set(cacheKey, data).catch(() => { /* IDB failure is non-fatal */ });
      } else {
        console.error('Failed to fetch places.');
      }
    } catch (error) {
      // Network failure — fall back to the last cached result for this cell.
      const cached = await db.get(cacheKey).catch(() => null);
      if (cached) {
        applyIncoming(cached);
      } else {
        console.error('Error fetching places (no cache available):', error);
        // Allow a retry on next center change
        lastSearchKey.current = null;
      }
    } finally {
      setLoading(false);
    }
  }, [mapCenter, routeMode, i18n.language, user]);
  // selectedList and zoom intentionally omitted:
  //   • selectedList changes are handled by the useMemo sort above.
  //   • zoom is not sent to the API, so a zoom change alone should not trigger
  //     a new fetch (the cell check already handles meaningful position changes).

  // Call this before fetchPlaces() when you need to bypass the cell cache —
  // e.g. after hiding a place so the updated list is fetched immediately.
  const invalidateSearch = useCallback(() => {
    lastSearchKey.current = null;
  }, []);

  const onCenterChanged = (event) => {
    const map = event.map;
    if (!map) return;

    const newZoom = map.getZoom();
    setZoom(newZoom);

    const newCenter = map.getCenter();
    if (!newCenter) return;

    if (!mapCenter) {
      setMapCenter({ lat: newCenter.lat, lng: newCenter.lng });
      return;
    }

    const oldLat = parseFloat(mapCenter.lat.toFixed(12));
    const oldLng = parseFloat(mapCenter.lng.toFixed(12));
    const newLat = parseFloat(newCenter.lat.toFixed(12));
    const newLng = parseFloat(newCenter.lng.toFixed(12));

    if (newLat !== oldLat || newLng !== oldLng) {
      setMapCenter({ lat: newCenter.lat, lng: newCenter.lng });
    }
  };

  const handleSearch = async (e, searchQuery) => {
    if (e.key !== 'Enter' || !searchQuery.trim()) return;
    try {
      const { lat, lng } = await geocodeAddress(searchQuery.trim());
      setMapCenter({ lat, lng });
    } catch (error) {
      console.error('Geocoding failed:', error);
    }
  };

  return {
    places,
    loading,
    zoom,
    mapCenter,
    setMapCenter,
    fetchPlaces,
    invalidateSearch,
    onCenterChanged,
    handleSearch,
  };
}
